<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { api } from '../api'
import { store } from '../store'

const form = reactive({ nickName: '', avatar: '', phone: '', gender: '保密' })
const loading = ref(true), saving = ref(false), uploading = ref(false), message = ref(''), error = ref('')
const avatarInput = ref(null)
const initials = computed(() => (form.nickName || '用户').slice(0, 1))
const registerTime = computed(() => {
  const value = store.profile?.createTime
  if (!value) return '暂无记录'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date)
})

const syncForm = profile => Object.assign(form, {
  nickName: profile?.nickName || '', avatar: profile?.avatar || '',
  phone: profile?.phone || '', gender: ['男', '女', '保密'].includes(profile?.gender) ? profile.gender : '保密'
})

const compressAvatar = file => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) return reject(new Error('请选择图片文件'))
  if (file.size > 10 * 1024 * 1024) return reject(new Error('原图不能超过 10MB'))
  const image = new Image(), url = URL.createObjectURL(file)
  image.onload = () => {
    const max = 512, ratio = Math.min(1, max / Math.max(image.width, image.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.width * ratio)); canvas.height = Math.max(1, Math.round(image.height * ratio))
    const context = canvas.getContext('2d'); context.drawImage(image, 0, 0, canvas.width, canvas.height)
    URL.revokeObjectURL(url)
    canvas.toBlob(blob => blob ? resolve(new File([blob], `avatar-${Date.now()}.jpg`, { type: 'image/jpeg' })) : reject(new Error('头像压缩失败')), 'image/jpeg', .82)
  }
  image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('图片读取失败')) }
  image.src = url
})

const chooseAvatar = async event => {
  const file = event.target.files?.[0]; if (!file) return
  error.value = ''; message.value = ''; uploading.value = true
  try { const compressed = await compressAvatar(file); form.avatar = await api.uploadAvatar(compressed); message.value = `头像已压缩至 ${Math.ceil(compressed.size / 1024)}KB，保存资料后生效` }
  catch (e) { error.value = e.message }
  finally { uploading.value = false; event.target.value = '' }
}

const save = async () => {
  error.value = ''; message.value = ''; saving.value = true
  try { const profile = await api.updateProfile(form); store.setProfile(profile); syncForm(profile); message.value = '个人资料已保存' }
  catch (e) { error.value = e.message }
  finally { saving.value = false }
}

onMounted(async () => {
  try { syncForm(store.profile || await store.loadProfile()) } catch (e) { error.value = e.message } finally { loading.value = false }
})
</script>

<template>
  <section class="profile-page">
    <header class="profile-heading"><div><h1>个人中心</h1><p>完善你的基本资料。手机号仅用于必要的服务联系，不会公开展示。</p></div><span v-if="store.membership" class="membership-chip">{{ store.membership.planCode }} 会员</span></header>
    <div v-if="loading" class="profile-loading"><span class="loader"></span><p>正在读取个人资料…</p></div>
    <form v-else class="profile-shell" @submit.prevent="save">
      <aside class="avatar-editor">
        <button type="button" class="avatar-preview" @click="avatarInput.click()" :aria-label="uploading ? '头像上传中' : '修改头像'">
          <img v-if="form.avatar" :src="form.avatar" alt="当前头像"><span v-else>{{ initials }}</span><i>{{ uploading ? '处理中…' : '修改' }}</i>
        </button>
        <input ref="avatarInput" type="file" accept="image/jpeg,image/png,image/webp" hidden @change="chooseAvatar">
        <h2>{{ form.nickName || '未设置昵称' }}</h2><p>支持 JPG、PNG、WebP<br>上传前自动压缩至 512px</p><div class="register-time"><span>注册时间</span><time>{{ registerTime }}</time></div>
      </aside>
      <div class="profile-fields">
        <div class="field-title"><div><h2>基本资料</h2><p>缺失的信息可以随时补充或修改。</p></div></div>
        <label><span>昵称</span><input v-model.trim="form.nickName" maxlength="40" required placeholder="请输入昵称"></label>
        <label><span>手机号</span><input v-model.trim="form.phone" inputmode="numeric" maxlength="11" placeholder="请输入 11 位手机号"></label>
        <div class="profile-field gender-field"><span class="field-label">性别</span><div class="gender-options"><label v-for="item in ['男','女','保密']" :key="item"><input v-model="form.gender" type="radio" :value="item"><span>{{ item }}</span></label></div></div>
        <div class="profile-feedback"><span class="success">{{ message }}</span><span class="error">{{ error }}</span></div>
        <div class="profile-actions"><button type="button" class="outline-button" @click="syncForm(store.profile)">取消修改</button><button class="button" :disabled="saving || uploading">{{ saving ? '保存中…' : '保存资料' }}</button></div>
      </div>
    </form>
  </section>
</template>
